<!--
  CollectorLineResult — what a marked line really came to (collector-clarity,
  handoff spec Section 6.4). The old chip «اتجاب ✓ · لقيت 2 من 3 · 160 ج.م»
  showed a price per piece that read as the amount paid, above a stale
  «3 × 150 = 450». This says the real sum, from the same rule the shop's paid
  figure uses (collector/dayModel.js `lineSpend`).

  The marked single card (the full form):
    collected   «اتجاب ✓» then «{qty} × {price} = {gross} ج.م»; a found
                quantity or a shelf price that differs from the order is amber
                («2 من 3», «160»); with her deposit «· دفعت {paid}» follows;
                and when anything differs, «المطلوب كان …» under it.
    not found   «مش موجود» and the sum struck through — nobody adds it to the
                bill.

  `row` is a member row of a grouped card (the short form): «اتجاب ✓ {paid}
  ج.م» with «{found} من {qty}» in amber when it differs; «مش موجود» and her
  amount struck through.

  A line the leader verified adds the lilac «راجعه الليدر ✓ · مقفول».
  Draws nothing for a line that is not marked. Spans, so it can sit inside a
  card's open target (a <button>). No state is carried by colour alone: every
  chip is a word.
-->
<template>
  <span v-if="marked || verified" class="c-result c-num" :class="{ 'c-result--row': row }">
    <span class="c-result__line">
      <template v-if="collected">
        <template v-if="row">
          <span class="c-chip c-chip--green c-status__chip">{{ spend.price > 0 ? t('collector.result.collectedPaid', { paid: pounds(paidCents) }) : t('collector.line.collectedChip') }}</span>
          <span v-if="qtyChanged" class="c-result__chg">{{ foundOf }}</span>
        </template>
        <template v-else>
          <span class="c-chip c-chip--green c-status__chip">{{ t('collector.line.collectedChip') }}</span>
          <!-- «</i18n-t> <span»: the space on that line is kept, so the sum and «· دفعت …» wrap apart -->
          <span class="c-result__sum">
            <i18n-t keypath="collector.result.sum" tag="span" scope="global">
              <template #qty>
                <span :class="{ 'c-result__chg': qtyChanged }">{{ qtyChanged ? foundOf : spend.qty }}</span>
              </template>
              <template #price>
                <span :class="{ 'c-result__chg': priceChanged }">{{ priceText(spend.price) }}</span>
              </template>
              <template #total>{{ totalText(spend.gross, spend.price) }}</template>
            </i18n-t> <span v-if="spend.deposit > 0" class="c-result__paid">· {{ t('collector.result.paid', { paid: pounds(paidCents) }) }}</span>
          </span>
        </template>
      </template>
      <template v-else-if="notFound">
        <span class="c-chip c-chip--red c-status__chip">{{ t('collector.line.oosChip') }}</span>
        <span v-if="listUnit > 0" class="c-result__struck">{{ orderedText }}</span>
      </template>
      <span v-if="verified" class="c-chip c-chip--lilac">{{ t('collector.line.verified') }}</span>
    </span>
    <span v-if="!row && collected && (qtyChanged || priceChanged)" class="c-result__was">
      {{ t('collector.result.was', { qty: orderedQty, price: priceText(listUnit), total: totalText(line.totalCents, listUnit) }) }}
    </span>
  </span>
</template>

<script setup>
import { computed } from 'vue';
import { I18nT, useI18n } from 'vue-i18n';
import { lineSpend } from '@/collector/dayModel.js';
import { isLiveLine } from '@/collector/lineView.js';
import { linePaidCents } from '@/collector/moneyView.js';
import { useCurrency } from '@/composables/useCurrency.js';

const props = defineProps({
  line: { type: Object, required: true },
  /** The short form of a grouped member row. */
  row: { type: Boolean, default: false },
});

const { t } = useI18n();
const { formatAmount } = useCurrency();
// Nothing is floored, so an amount can go below zero: the sign is U+2212 and
// stays on its digits (Intl keeps it inside its LRM).
const pounds = (cents) => formatAmount(cents, { trimZeros: true }).replace('-', '−');
// A line with no price says «—», never a made-up zero.
const priceText = (cents) => (cents > 0 ? pounds(cents) : '—');
const totalText = (cents, unitCents) => (unitCents > 0 ? pounds(cents) : '—');

const live = computed(() => isLiveLine(props.line));
const collected = computed(() => live.value && props.line.outcome === 'collected');
const notFound = computed(() => live.value && props.line.outcome === 'out_of_stock');
const marked = computed(() => collected.value || notFound.value);
const verified = computed(() => live.value && props.line.leaderState?.verified === true);

/** What his mark stands for: found quantity × shelf price, and her deposit inside it. */
const spend = computed(() => lineSpend(props.line));
/** What he paid: the ONE rule lives in collector/moneyView.js. */
const paidCents = computed(() => linePaidCents(props.line));
const orderedQty = computed(() => Number(props.line.qty) || 0);
const listUnit = computed(() => Number(props.line.unitPriceCents) || 0);
const qtyChanged = computed(() => collected.value && spend.value.qty !== orderedQty.value);
const priceChanged = computed(() => collected.value && spend.value.price !== listUnit.value);

/** «2 من 3» */
const foundOf = computed(() => t('collector.figures.foundOf', { found: spend.value.qty, qty: orderedQty.value }));

/** What the order came to, struck through on a line he did not find. */
const orderedText = computed(() =>
  props.row
    ? t('collector.line.price', { price: pounds(props.line.totalCents) })
    : t('collector.result.sum', { qty: orderedQty.value, price: pounds(listUnit.value), total: pounds(props.line.totalCents) }),
);
</script>

<style scoped>
.c-result {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.c-result__line {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  font-size: 15px;
  font-weight: 700;
  color: var(--c-ink);
}

.c-result--row .c-result__line { font-size: 13px; }

.c-status__chip {
  padding: 3px 10px;
}

/* A figure that differs from the order. */
.c-result__chg {
  padding: 0 5px;
  border-radius: 6px;
  background: var(--c-amber-fill);
  color: var(--c-amber-text);
}

/* «· دفعت 205» wraps as one piece, never with its number alone on the next line */
.c-result__paid { white-space: nowrap; }

.c-result__struck {
  font-size: 14px;
  font-weight: 500;
  color: var(--c-muted);
  text-decoration: line-through;
}

.c-result--row .c-result__struck { font-size: 13px; }

.c-result__was {
  font-size: 12px;
  color: var(--c-muted);
}
</style>
